import { Component, OnInit } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { RememberService } from '@app/core/services/remember.service';
import { TranslationItem, TranslationLanguage, TranslationListService, TranslationsGet, TranslationType } from '@app/services/translation-list.service';

@Component({
    selector: 'tt-translate',
    templateUrl: './translate.component.html',
    styleUrls: ['./translate.component.css'],
    standalone: false,
})
export class TranslateComponent implements OnInit {
    public page?: TranslationsGet;
    public languages: TranslationLanguage[] = [];
    public translations: TranslationItem[] = [];
    public searchTranslations: TranslationItem[] = [];
    public translateType: TranslationType = 'SEARCH';
    public retrieving = false;

    public readonly filterSearchButtons: FormButton[] = [{ id: 'erase', icon: 'fas fa-eraser', type: 'danger', onClick: () => this.resetSearch() }];
    public readonly searchTextButtons: FormButton[] = [
        { id: 'erase', icon: 'fas fa-eraser', type: 'danger', onClick: () => this.resetSearch(true) },
        { id: 'search', icon: 'fas fa-search', type: 'primary', onClick: () => this.searchTranslation() },
    ];

    constructor(private translationsService: TranslationListService, private remember: RememberService) {}

    public async ngOnInit(): Promise<void> {
        await Promise.all([this.loadPage(), this.loadLanguages()]);
    }

    public async onTranslateTypeChanged(value: string): Promise<void> {
        if (!this.page) return;
        if (!this.isTranslationType(value)) return;
        this.translateType = value;
        this.page.bg_translate_type = value;
        await this.rememberValue('bg_translate_type');
        await this.retrieveChanges();
    }

    public async onPageChanged(value: string, field: 'language_id_to_translate' | 'language_id_display' | 'word_last_used_dt'): Promise<void> {
        if (!this.page || value === this.page[field]) return;
        this.page[field] = value;
        if (this.hasValue(value)) await this.rememberValue(field);
    }

    public onSearchChanged(value: string): void {
        if (!this.page || value === this.page.searchtext) return;
        this.page.searchtext = value;
        if (value === '') void this.rememberValue('searchtext');
    }

    public onSearchBlur(): void {
        if (this.hasValue(this.page?.searchtext)) void this.searchTranslation();
    }

    public async retrieveChanges(): Promise<void> {
        if (!this.page) return;
        this.retrieving = true;
        try {
            if (this.page.bg_translate_type === 'SEARCH') {
                await this.loadSearchTranslations();
            } else {
                await this.loadTranslations();
            }
        } finally {
            this.retrieving = false;
        }
    }

    public async searchTranslation(): Promise<void> {
        if (!this.page) return;
        if (this.page.searchtext > '') await this.rememberValue('searchtext');
        await this.loadSearchTranslations();
    }

    public resetSearch(clearSearchResults = false): void {
        if (!this.page) return;
        this.page.searchtext = '';
        void this.rememberValue('searchtext');
        if (clearSearchResults) this.searchTranslations = [];
    }

    private async loadPage(): Promise<void> {
        this.page = await this.translationsService.getTranslationsPage();
        this.translateType = this.page.bg_translate_type;
        await Promise.all([this.loadTranslations(), this.loadSearchTranslations()]);
    }

    private async loadLanguages(): Promise<void> {
        this.languages = await this.translationsService.getLanguages();
    }

    private async loadTranslations(): Promise<void> {
        if (!this.page || !this.hasRequiredValues()) return;
        this.translations = [];
        this.translations = await this.translationsService.getTranslations(this.page);
    }

    private async loadSearchTranslations(): Promise<void> {
        if (!this.page || !this.hasRequiredValues()) return;
        this.searchTranslations = [];
        this.searchTranslations = await this.translationsService.getSearchTranslations(this.page);
    }

    private hasRequiredValues(): boolean {
        return this.hasValue(this.page?.language_id_to_translate) && this.hasValue(this.page?.language_id_display) && this.hasValue(this.page?.word_last_used_dt) && this.hasValue(this.page?.bg_translate_type) && this.page?.searchtext !== undefined;
    }

    private hasValue(value: string | undefined): boolean {
        return !!value?.trim();
    }

    private isTranslationType(value: string): value is TranslationType {
        return value === 'SEARCH' || value === 'NOTTRANSLATED' || value === 'TRANSLATED';
    }

    private rememberValue(field: keyof TranslationsGet): Promise<void> {
        if (!this.page) return Promise.resolve();
        return this.remember.remember(`w_translate.${field}`, this.page[field]);
    }
}
